import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { useLanguage } from "../lib/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Corporate() {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", occasion: "", quantity: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/quotes`, { ...form, lang });
      setStatus("success");
      setForm({ name: "", email: "", phone: "", company: "", occasion: "", quantity: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="corporate" data-testid="corporate-section" className="bg-cream py-28 md:py-40">
      <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20 grid md:grid-cols-12 gap-10 md:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 1 }}
          className="md:col-span-5"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-px bg-terracotta" />
            <span className="eyebrow">{t.corporate.eyebrow}</span>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl leading-[1.05] tracking-tight text-ink mb-8">
            {t.corporate.title}
          </h2>
          <p className="font-body text-base md:text-lg leading-relaxed text-ink-soft max-w-md">
            {t.corporate.sub}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 1.1, delay: 0.1 }}
          className="md:col-span-7"
        >
          <form onSubmit={submit} className="space-y-7" data-testid="quote-form">
            <div className="grid sm:grid-cols-2 gap-7">
              <Field id="name" label={t.corporate.form.name} required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="quote-name" />
              <Field id="email" label={t.corporate.form.email} type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="quote-email" />
              <Field id="phone" label={t.corporate.form.phone} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="quote-phone" />
              <Field id="company" label={t.corporate.form.company} value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="quote-company" />
              <Field id="occasion" label={t.corporate.form.occasion} value={form.occasion} onChange={(v) => setForm({ ...form, occasion: v })} testid="quote-occasion" />
              <Field id="quantity" label={t.corporate.form.quantity} value={form.quantity} onChange={(v) => setForm({ ...form, quantity: v })} testid="quote-quantity" />
            </div>
            <Field id="message" label={t.corporate.form.message} multiline value={form.message} onChange={(v) => setForm({ ...form, message: v })} testid="quote-message" />

            <div className="pt-6 flex items-center gap-6 flex-wrap">
              <button
                type="submit"
                disabled={status === "sending"}
                data-testid="quote-submit"
                className="inline-flex items-center gap-3 bg-ink hover:bg-dustyrose-deep text-cream px-10 py-4 text-[11px] tracking-[0.28em] uppercase font-semibold transition-all duration-500 disabled:opacity-60"
              >
                {status === "sending" ? t.corporate.form.sending : t.corporate.form.submit}
              </button>
              {status === "success" && (
                <p data-testid="quote-success" className="font-body italic text-botanical text-sm">{t.corporate.form.success}</p>
              )}
              {status === "error" && (
                <p data-testid="quote-error" className="font-body italic text-terracotta text-sm">{t.corporate.form.error}</p>
              )}
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
}

function Field({ id, label, type = "text", value, onChange, required, multiline, testid }) {
  const props = {
    id,
    "data-testid": testid,
    required,
    value,
    onChange: (e) => onChange(e.target.value),
    placeholder: " ",
    className: "editorial-input peer",
  };
  return (
    <div className="relative">
      {multiline ? <textarea rows={3} {...props} className="editorial-input peer resize-none" /> : <input type={type} {...props} />}
      <label
        htmlFor={id}
        className="absolute left-0 top-3 text-ink-soft font-body text-sm pointer-events-none transition-all duration-300
                   peer-focus:-top-2 peer-focus:text-[10px] peer-focus:tracking-[0.25em] peer-focus:uppercase peer-focus:text-terracotta
                   peer-[:not(:placeholder-shown)]:-top-2 peer-[:not(:placeholder-shown)]:text-[10px] peer-[:not(:placeholder-shown)]:tracking-[0.25em] peer-[:not(:placeholder-shown)]:uppercase"
      >
        {label}
      </label>
    </div>
  );
}
